import { ArrowLeft, Database, HardDrive, Image, FileText, MessageSquare, Trash2 } from "lucide-react";
import { useState } from "react";
import { BottomNav } from "./bottom-nav";
import { SettingsSection } from "./settings";

interface DataUsagePageProps {
  onBack: () => void;
  activeTab: "home" | "favorites" | "wallet";
  onTabChange: (tab: string) => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
}

export function DataUsagePage({
  onBack,
  activeTab,
  onTabChange,
  favoritesCount,
  onMenuClick,
  onPostAdClick,
  postAdBlockedByUnverifiedEmail = false,
}: DataUsagePageProps) {
  /** Not measured in-app yet; show zeros until local storage / Cache API reporting exists */
  const dataUsage = {
    images: 0,
    cache: 0,
    documents: 0,
    messages: 0,
    total: 0,
  };

  const [autoDownloadImages, setAutoDownloadImages] = useState(true);
  const [autoDownloadVideos, setAutoDownloadVideos] = useState(false);
  const [highQualityImages, setHighQualityImages] = useState(true);
  const [offlineMode, setOfflineMode] = useState(false);

  const handleClearCache = () => {
    if (confirm("Deseja limpar o cache do aplicativo?\n\nIsso pode melhorar o desempenho, mas você precisará baixar alguns dados novamente.")) {
      alert(`Cache limpo com sucesso!\n\n${dataUsage.cache.toFixed(1)} MB de espaço liberado.`);
    }
  };

  const handleClearImages = () => {
    if (confirm("Deseja excluir todas as imagens em cache?\n\nAs imagens serão baixadas novamente quando necessário.")) {
      alert(`Imagens em cache excluídas!\n\n${dataUsage.images.toFixed(1)} MB de espaço liberado.`);
    }
  };

  const handleClearAllData = () => {
    if (confirm(
      "ATENÇÃO: Limpar Todos os Dados\n\n" +
      "Esta ação irá:\n" +
      "• Excluir cache e dados temporários\n" +
      "• Remover imagens baixadas\n" +
      "• Limpar histórico local\n" +
      "• Manter sua conta e configurações\n\n" +
      `Total de espaço a ser liberado: ${dataUsage.total.toFixed(1)} MB\n\n` +
      "Deseja continuar?"
    )) {
      alert("Todos os dados locais foram limpos com sucesso!");
    }
  };

  const storageSection = {
    title: "Uso de Armazenamento",
    items: [
      {
        icon: Image,
        label: "Imagens em Cache",
        subtitle: dataUsage.images > 0 ? `${dataUsage.images.toFixed(1)} MB` : "—",
        action: handleClearImages,
      },
      {
        icon: Database,
        label: "Cache do Aplicativo",
        subtitle: dataUsage.cache > 0 ? `${dataUsage.cache.toFixed(1)} MB` : "—",
        action: handleClearCache,
      },
      {
        icon: FileText,
        label: "Documentos e Dados",
        subtitle: dataUsage.documents > 0 ? `${dataUsage.documents.toFixed(1)} MB` : "—",
        action: () => alert("Visualizando documentos..."),
      },
      {
        icon: MessageSquare,
        label: "Mensagens",
        subtitle: dataUsage.messages > 0 ? `${dataUsage.messages.toFixed(1)} MB` : "—",
        action: () => alert("Gerenciar mensagens..."),
      },
    ],
  };

  const downloadSection = {
    title: "Download Automático",
    items: [
      {
        icon: Image,
        label: "Imagens",
        subtitle: autoDownloadImages ? "Sempre baixar" : "Apenas em Wi-Fi",
        action: () => setAutoDownloadImages((prev) => !prev),
        toggle: true,
        value: autoDownloadImages,
      },
      {
        icon: FileText,
        label: "Vídeos",
        subtitle: autoDownloadVideos ? "Sempre baixar" : "Nunca baixar automaticamente",
        action: () => setAutoDownloadVideos((prev) => !prev),
        toggle: true,
        value: autoDownloadVideos,
      },
    ],
  };

  const qualitySection = {
    title: "Qualidade de Mídia",
    items: [
      {
        icon: Image,
        label: "Imagens em Alta Qualidade",
        subtitle: highQualityImages ? "Usar alta resolução" : "Economizar dados",
        action: () => setHighQualityImages((prev) => !prev),
        toggle: true,
        value: highQualityImages,
      },
      {
        icon: HardDrive,
        label: "Modo Offline",
        subtitle: offlineMode ? "Salvar para uso offline" : "Desativado",
        action: () => setOfflineMode((prev) => !prev),
        toggle: true,
        value: offlineMode,
      },
    ],
  };

  const maintenanceSection = {
    title: "Manutenção",
    items: [
      {
        icon: Trash2,
        label: "Limpar Todos os Dados",
        subtitle: dataUsage.total > 0 ? `Liberar ${dataUsage.total.toFixed(1)} MB` : "Sem dados medidos ainda",
        action: handleClearAllData,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 px-4 py-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors -ml-2"
              aria-label="Voltar"
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <h1 className="text-lg font-medium text-white">Uso de Dados</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto">
        {/* Storage Overview */}
        <div className="mx-4 mt-4 bg-card border border-border rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-medium text-foreground">Armazenamento Total</h3>
              <p className="text-sm text-muted-foreground">Dados locais do aplicativo</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-medium text-primary">
                {dataUsage.total > 0 ? dataUsage.total.toFixed(1) : "—"}
              </div>
              <div className="text-sm text-muted-foreground">MB</div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            A medição de armazenamento local no navegador ainda não está ativa; os valores abaixo são placeholders.
          </p>

          {/* Storage Bar */}
          <div className="space-y-2">
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full"
                style={{
                  width:
                    dataUsage.total > 0
                      ? `${(dataUsage.images / dataUsage.total) * 100}%`
                      : "0%",
                }}
              />
            </div>
            <div className="flex flex-wrap gap-4 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-primary rounded-sm" />
                <span className="text-muted-foreground">
                  Imagens (
                  {dataUsage.total > 0 ? ((dataUsage.images / dataUsage.total) * 100).toFixed(0) : "0"}%)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-secondary rounded-sm" />
                <span className="text-muted-foreground">
                  Cache (
                  {dataUsage.total > 0 ? ((dataUsage.cache / dataUsage.total) * 100).toFixed(0) : "0"}%)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-accent rounded-sm" />
                <span className="text-muted-foreground">Outros</span>
              </div>
            </div>
          </div>
        </div>

        {/* Settings Sections */}
        <div className="px-4 space-y-6 mt-6">
          <SettingsSection
            title={storageSection.title}
            items={storageSection.items}
          />
          <SettingsSection
            title={downloadSection.title}
            items={downloadSection.items}
          />
          <SettingsSection
            title={qualitySection.title}
            items={qualitySection.items}
          />
          <SettingsSection
            title={maintenanceSection.title}
            items={maintenanceSection.items}
          />
        </div>

        {/* Tips */}
        <div className="px-4 mt-6 mb-8">
          <div className="bg-secondary/30 border border-primary/20 rounded-xl p-4">
            <h3 className="font-medium text-foreground mb-2">
              💡 Dicas para Economizar Dados
            </h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex gap-2">
                <span className="text-primary">•</span>
                <span>Desative o download automático de imagens em dados móveis</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">•</span>
                <span>Use imagens de menor qualidade para economizar espaço</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">•</span>
                <span>Limpe o cache regularmente para liberar espaço</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">•</span>
                <span>Conecte-se a uma rede Wi-Fi para downloads grandes</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav
        activeTab={activeTab}
        onTabChange={onTabChange}
        favoritesCount={favoritesCount}
        onMenuClick={onMenuClick}
        onPostAdClick={onPostAdClick}
        postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
      />
    </div>
  );
}
